import type {IReactionDisposer} from 'mobx';

import dayjs from 'dayjs';
import {makeAutoObservable, reaction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {ConferenceApiModel} from '../../../../api/conference';
import type {EventType, NotificationEvent} from '../../../../api/event';
import type {ClientTaskApiModel} from '../../../../api/task';
import type {CoreUserEventsListParams} from '../../../../api/types/core/methods';
import type {Config, Section} from './types';

import {conferenceStore} from '../../../../api/conference';
import {notificationEventExpand} from '../../../../api/event';
import {membershipStore} from '../../../../api/membership';
import {taskStore} from '../../../../api/task';
import {createCollectionStore} from '../../../../api/utils/collectionStore';
import {ISOStringToDatetimeObj} from '../../../../utils/datetime';
import {
  isConferenceEnded,
  isConferenceExpired,
} from '../../../../utils/entity/conference';
import {envManager} from '../../../../utils/env';
import {findContext} from '../../utils';
import {
  DEFAULT_CURSOR,
  filterEvent,
  getExcludedEvents,
  LIMIT,
  TODAY_EVENTS_LIMIT,
} from './utils';

/**
 * Notifications store
 * The store contains logic of listing notifications
 */
export class NotificationsStore {
  earlierEventsStore = createCollectionStore<
    NotificationEvent,
    CoreUserEventsListParams
  >({
    method: 'user.events.list',
    params: {
      cursor: DEFAULT_CURSOR,
      expand: notificationEventExpand,
      query: [
        [
          'type',
          '!in',
          [
            ...getExcludedEvents(),
            'conference_upcoming',
            'conference_pending',
            'conference_started',
            'conference_stopped',
            'task_pending',
            'payment_upcoming',
          ].concat(
            envManager.isCore
              ? ['conference_upcoming_5min', 'conference_taken']
              : [],
          ),
        ],
        [
          'created',
          '<',
          ISOStringToDatetimeObj(dayjs().startOf('day').toISOString()),
        ],
      ],
    },
  });
  limit: Config['limit'];
  todayEventsStore = createCollectionStore<
    NotificationEvent,
    CoreUserEventsListParams
  >({
    method: 'user.events.list',
    params: {
      cursor: DEFAULT_CURSOR,
      expand: notificationEventExpand,
      limit: TODAY_EVENTS_LIMIT,
      query: [
        ['type', '!in', getExcludedEvents()],
        [
          'created',
          '>=',
          ISOStringToDatetimeObj(dayjs().startOf('day').toISOString()),
        ],
      ],
    },
  });
  private disposers: IReactionDisposer[] = [];

  constructor(config?: Config) {
    this.limit = config?.limit || LIMIT;

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );

    this.disposers.push(
      ...[
        reaction(
          () => taskStore.updating,
          updating => {
            if (updating.success) {
              this.fetch(true);
            }
          },
        ),
        reaction(
          () => conferenceStore.coach.updating,
          updating => {
            if (updating.success) {
              this.fetch(true);
            }
          },
        ),
        reaction(
          () => membershipStore.client.declining,
          declining => {
            if (declining.success) {
              this.fetch(true);
            }
          },
        ),
        reaction(
          () =>
            this.todayEventsStore.isLoaded && this.earlierEventsStore.isLoaded,
          isLoaded => {
            if (
              isLoaded &&
              this.itemsCount === 0 &&
              this.earlierEventsStore.cursor.next
            ) {
              this.fetchMore();
            }
          },
        ),
      ],
    );
  }

  destruct() {
    this.disposers.forEach(dispose => {
      dispose();
    });

    this.todayEventsStore.clear();
    this.earlierEventsStore.clear();
  }

  get isEmpty() {
    return (
      this.todayEventsStore.isLoaded &&
      this.earlierEventsStore.isLoaded &&
      this.sections.every(section => !section.data.length)
    );
  }

  get isFetching() {
    return (
      !this.todayEventsStore.isLoaded ||
      this.todayEventsStore.isFetching ||
      !this.earlierEventsStore.isLoaded ||
      this.earlierEventsStore.isFetching
    );
  }

  get itemsCount() {
    return this.sections.reduce((sum, section) => sum + section.data.length, 0);
  }

  get sections() {
    const sections: Section[] = [];

    if (!this.todayEventsStore.isLoaded && !this.earlierEventsStore.isLoaded) {
      return [];
    }

    const todayEvents = this.todayEventsStore.items
      .slice()
      .filter(event => {
        if (
          (['task_overdue', 'task_pending'] as EventType[]).includes(event.type)
        ) {
          const task = findContext<ClientTaskApiModel>(event, 'task');

          if (task && task.done) {
            return false;
          }
        }

        if (
          (
            [
              'conference_pending',
              'conference_upcoming',
              'conference_upcoming_5min',
              'conference_started',
              'conference_stopped',
              'conference_taken',
            ] as EventType[]
          ).includes(event.type)
        ) {
          const conference = findContext<ConferenceApiModel>(
            event,
            'conference',
          );

          if (
            conference &&
            (isConferenceEnded(conference) || isConferenceExpired(conference))
          ) {
            return false;
          }
        }

        return filterEvent(event);
      })
      .map(event => ({
        ...event,
        __updaterField: Date.now(),
      }));

    const earlierEvents = this.earlierEventsStore.items
      .slice()
      .filter(event => {
        if (event.type === 'task_overdue') {
          const task = findContext<ClientTaskApiModel>(event, 'task');

          if (task && task.done) {
            return false;
          }
        }

        return filterEvent(event);
      });

    if (earlierEvents.length) {
      sections.push({
        badge: 0,
        data: earlierEvents,
        title: i18n.t('shared:earlier'),
      });
    }

    if (todayEvents.length) {
      sections.unshift({
        badge: todayEvents.length,
        data: todayEvents,
        title: i18n.t('shared:today'),
      });
    }

    return sections;
  }

  fetch(silent = false) {
    if (!silent) {
      this.todayEventsStore.isFetching = true;
      this.earlierEventsStore.isFetching = true;
    }

    this.todayEventsStore.fetch(
      {
        limit: Math.max(this.todayEventsStore.items.length, TODAY_EVENTS_LIMIT),
      },
      {silent},
    );

    this.earlierEventsStore.fetch(
      {
        limit: Math.max(this.earlierEventsStore.items.length, this.limit!),
      },
      {silent},
    );
  }

  async fetchMore() {
    const itemsCount = this.itemsCount;

    if (this.earlierEventsStore.cursor.next) {
      await this.earlierEventsStore.fetch(
        {
          cursor: this.earlierEventsStore.cursor.next,
          limit: this.limit!,
        },
        {
          appendTo: 'end',
          silent: true,
        },
      );

      const newItemsCount = this.sections.reduce(
        (sum, section) => sum + section.data.length,
        0,
      );

      if (itemsCount === newItemsCount) {
        this.fetchMore();
      }
    }
  }
}
